/**
 * Document-level shared `<defs>` host for paint servers whose content is
 * identical across every card that uses them — the back dot pattern, the
 * static (non-reveal) trump gradients, the game-type pip gradients. Minting
 * one copy instead of one-per-card lets Chrome share the rasterized paint
 * server across cards (`url(#id)` resolves document-wide for inline SVG, so
 * a card's own `<defs>` was never required — see [[todo/perf-shared-svg-defs]]).
 *
 * Lazily created on first use, not at module load, so tests that never touch
 * the DOM (or run outside jsdom) don't need boot wiring.
 *
 * Hidden with `position:absolute` + zero size + `overflow:hidden` — NOT
 * `display:none`, which some browsers skip when resolving paint servers,
 * leaving referencing elements unpainted.
 *
 * Invariant: the host rides `document.body` and is cached (with the suit-keyed
 * id caches in cards.js / card-backs.js / card-gradients.js) without an
 * is-still-attached check. That holds because full re-renders clear only the
 * app element (`game.js#clearAppForRender` → `appEl.replaceChildren()`), never
 * `document.body` — a path that wipes body would orphan the host while the
 * caches keep reporting its ids as live, silently blanking every reference.
 */
import { SVG_NS } from "./card-svg.js";

let sharedDefs = null;

export function getSharedDefs() {
  if (sharedDefs != null) return sharedDefs;
  const host = document.createElementNS(SVG_NS, "svg");
  host.setAttribute("width", "0");
  host.setAttribute("height", "0");
  host.style.position = "absolute";
  host.style.overflow = "hidden";
  sharedDefs = document.createElementNS(SVG_NS, "defs");
  host.appendChild(sharedDefs);
  document.body.appendChild(host);
  return sharedDefs;
}
